import { posthogEvents } from '@/shared/constants/constants';
import { posthogLog } from '@/shared/pinia/modules/posthog/logger';

const TRACK_SECTION_SELECTOR = '[data-track-section]';
const SETTINGS_KEYS_TO_STRIP = ['enable_tracking', 'tracking_label'];

const parseSettings = (raw: string): Record<string, unknown> => {
  try {
    const parsed = JSON.parse(raw);
    SETTINGS_KEYS_TO_STRIP.forEach((key) => delete parsed[key]);
    return parsed;
  } catch {
    return {};
  }
};

const CAROUSEL_NAV_NAMES = ['previous', 'next'];

const isCarouselNavClick = (target: Element): boolean => {
  const control = target.closest('button, a');
  if (!control) return false;
  const name = control.getAttribute('name') ?? '';
  const ariaLabel = control.getAttribute('aria-label') ?? '';
  return (
    CAROUSEL_NAV_NAMES.includes(name.toLowerCase()) ||
    CAROUSEL_NAV_NAMES.some((n) => ariaLabel.toLowerCase().includes(n))
  );
};

const getClickTarget = (target: EventTarget | null): string => {
  if (!(target instanceof Element)) return 'unknown';
  if (isCarouselNavClick(target)) return '';
  if (target.tagName === 'A' || target.closest('a')) return 'link';
  if (target.tagName === 'BUTTON' || target.closest('button')) return 'button';
  if (target.tagName === 'IMG') return 'image';
  return target.tagName.toLowerCase();
};

const getClickUrl = (target: EventTarget | null): string => {
  if (!(target instanceof Element)) return '';
  const anchor = target.tagName === 'A' ? target : target.closest('a');
  return anchor instanceof HTMLAnchorElement ? anchor.href : '';
};

type SectionTracker = {
  observer: IntersectionObserver;
  tracked: WeakSet<HTMLElement>;
  viewedSections: Set<string>;
  pendingTimers: Map<string, ReturnType<typeof setTimeout>>;
};

let tracker: SectionTracker | null = null;

const buildData = (el: HTMLElement) => ({
  section_type: el.dataset.trackSectionType || '',
  section_id: el.dataset.trackSectionId || '',
  section_position:
    Array.from(
      document.querySelectorAll<HTMLElement>(TRACK_SECTION_SELECTOR)
    ).indexOf(el) + 1,
  page_type: document.body.dataset.pageType || '',
  page_path: window.location.pathname,
  content_label: el.dataset.trackContentLabel || '',
  section_settings: parseSettings(el.dataset.trackSectionSettings || '{}')
});

const createTracker = (): SectionTracker => {
  const viewedSections = new Set<string>();
  const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const el = entry.target as HTMLElement;
        const sectionId = el.dataset.trackSectionId || '';

        if (entry.isIntersecting) {
          if (viewedSections.has(sectionId) || pendingTimers.has(sectionId))
            return;
          const timer = setTimeout(() => {
            pendingTimers.delete(sectionId);
            if (viewedSections.has(sectionId)) return;
            viewedSections.add(sectionId);
            const data = buildData(el);
            posthogLog('section_viewed', data);
            window.posthog?.capture(posthogEvents.SECTION_VIEWED, data);
          }, 1000);
          pendingTimers.set(sectionId, timer);
        } else {
          const timer = pendingTimers.get(sectionId);
          if (timer) {
            clearTimeout(timer);
            pendingTimers.delete(sectionId);
          }
        }
      });
    },
    { threshold: 0.5 }
  );

  return { observer, tracked: new WeakSet(), viewedSections, pendingTimers };
};

export const trackSection = (section: HTMLElement) => {
  tracker ??= createTracker();
  if (tracker.tracked.has(section)) return;
  tracker.tracked.add(section);

  tracker.observer.observe(section);

  section.addEventListener('click', (event) => {
    // Relewise tiles live in shadow roots, where event.target is retargeted
    // to the widget host; the composed path still starts at the real element.
    const origin = event.composedPath()[0] ?? event.target;
    const clickTarget = getClickTarget(origin);
    if (!clickTarget) return;
    const data = {
      ...buildData(section),
      click_target: clickTarget,
      click_url: getClickUrl(origin)
    };
    posthogLog('section_clicked', data);
    window.posthog?.capture(posthogEvents.SECTION_CLICKED, data);
  });
};

export const initSectionTracking = () => {
  document
    .querySelectorAll<HTMLElement>(TRACK_SECTION_SELECTOR)
    .forEach(trackSection);
};
